// 2) Third-party library imports
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    clone,
    concat,
    get,
    isEqual,
    keyBy,
    merge,
    uniqWith,
    update,
    values
} from 'lodash-es';

// 3d) Utility functions
import { DynamicObject } from '../../utils/commonInterfaces';

// 3e) Redux actions & types
import { ChangeLine, ChangesState, GetChangesRequestPayload } from './types';
import type { RootState } from '../storeTypes';
import { PageData } from '../common/types';

// 3f) Project-specific modules - components / types
import { CHANGE_PAGE, DETAILS_TAB } from 'src/config/tableAndPageConstants';
import { customersSortByOptionsMap } from 'src/constants/customersSortAndFilters';

// Type-safe initialState!
export const initialState: ChangesState = {
    loading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: CHANGE_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {},
    tableFilter: undefined,
    activeData: {
        record: {},
        loading: false,
        selectedId: null,
        errorMessages: [],
        filters: {},
        customers: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CUSTOMER_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                Customer: '',
                CustomerCode: ''
            },
            customerState: '',
            sortBy: customersSortByOptionsMap.customerCode.value,
            sortAscending: true,
        },
    }
};

const changeSlice = createSlice({
    name: 'change',
    initialState,
    reducers: {
        deallocateRequestAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    payload: {};
                    callback?: (response: DynamicObject) => void;
                }>) => {
                // No state update needed because the original action was never handled in the reducer.
            },
            prepare: (
                payload: {},
                callback?: (response: DynamicObject) => void
            ) => ({
                payload: {
                    payload,
                    callback,
                }
            })
        },
        getChangesRequestAction: (
            state,
            _action: PayloadAction<GetChangesRequestPayload>
        ) => {
            state.loading = true;
        },
        getChangesSuccessAction: (
            state,
            action: PayloadAction<{
                data: ChangeLine[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                const existingData = state.data || [];
                state.data = uniqWith([...existingData, ...action.payload.data], isEqual);
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = [];
        },
        getChangesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        updateChangesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        updateChangesFiltersAction: (
            state,
            action: PayloadAction<GetChangesRequestPayload['filters']>
        ) => {
            const nextFilters = action.payload;
            const isReset = !nextFilters || Object.keys(nextFilters).length === 0;

            const mergedFilters = isReset
                ? initialState.filters
                : { ...state.filters, ...nextFilters };
                
            state.filters = mergedFilters;
            state.activeData.filters = mergedFilters
        },
        setSelectedChangeIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    changeId: string | null;
                    callback: () => void
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                changeId: string | null,
                callback: () => void
            ) => {
                return {
                    payload: { changeId, callback }
                }
            }
        },
        setSelectedChangeIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getChangeDataRequestAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getChangeDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = initialState.activeData.errorMessages;
        },
        getChangeDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
        },
        clearChangeDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        clearChangeFiltersAction: (state) => {
            state.filters = initialState.filters;
        },
    }
});

const { actions, reducer } = changeSlice;

// Actions
export const {
    deallocateRequestAction,
    getChangesRequestAction,
    getChangesSuccessAction,
    getChangesErrorAction,
    updateChangesTableFilterAction,
    updateChangesFiltersAction,
    setSelectedChangeIdRequestAction,
    setSelectedChangeIdSuccessAction,
    getChangeDataRequestAction,
    getChangeDataSuccessAction,
    getChangeDataErrorAction,
    clearChangeDataAction,
    clearChangeFiltersAction
} = actions;

// Selectors
export const getChangesState = (state: RootState) => state.changes;
export const getChangeData = createSelector(
    getChangesState,
    (change) => change.activeData
);
export const getSelectedChangeId = createSelector(
    getChangeData,
    (activeData) => activeData.selectedId
);

// Reducer
export default reducer;
